import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers';
import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { APIRoutes } from 'constants/';
import { getCSRFToken } from 'helpers/';
import { logout, setIsLoggedIn } from '../slices';
import {
  getRetryDelayMs,
  is429Error,
  MAX_RETRIES,
  waitWithAbort,
} from './retry429';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_PUBLIC_API_HOST,
  credentials: 'include',
  prepareHeaders: (headers) => {
    headers.set('X-CSRFToken', getCSRFToken());
    return headers;
  },
});

let refreshPromise:
  | MaybePromise<QueryReturnValue<any, FetchBaseQueryError, FetchBaseQueryMeta>>
  | undefined;

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const executeWithReauth = async () => {
    let result = await baseQuery(args, api, extraOptions);

    if (
      result.error &&
      [401, 403].includes(result.error.status as number) &&
      ![
        APIRoutes.RefreshTokens,
        APIRoutes.SingUp,
        APIRoutes.LogIn,
        APIRoutes.ResetPasswordInitiate,
        APIRoutes.ResetPasswordSubmit,
        APIRoutes.SupplierLogIn,
        APIRoutes.SupplierSignUp,
        APIRoutes.CommitmentLoginFromOutcome,
      ].includes((args as FetchArgs).url as APIRoutes)
    ) {
      const isPrimaryRefresh = refreshPromise === undefined;

      try {
        refreshPromise =
          refreshPromise ||
          baseQuery(
            {
              url: APIRoutes.RefreshTokens,
              method: 'GET',
            },
            api,
            extraOptions
          );

        const refreshResult: QueryReturnValue<
          any,
          FetchBaseQueryError,
          FetchBaseQueryMeta
        > = await refreshPromise;

        if (!refreshResult.error) {
          if (isPrimaryRefresh) {
            api.dispatch(setIsLoggedIn(true));
          }
          result = await baseQuery(args, api, extraOptions);
        } else {
          document.cookie = document.cookie.replace(
            /csrftoken=\w+/,
            'csrftoken=""'
          );
          api.dispatch(logout());
        }
      } finally {
        refreshPromise = undefined;
      }
    }

    return result;
  };

  const retry429 = async (
    result: Awaited<ReturnType<typeof executeWithReauth>>,
    attempt: number
  ): Promise<Awaited<ReturnType<typeof executeWithReauth>>> => {
    if (!is429Error(result.error) || attempt > MAX_RETRIES) {
      return result;
    }

    const retryDelayMs = getRetryDelayMs(result.meta, attempt);
    const shouldContinue = await waitWithAbort(retryDelayMs, api.signal);

    if (!shouldContinue) {
      return result;
    }

    const nextResult = await executeWithReauth();
    return retry429(nextResult, attempt + 1);
  };

  const result = await executeWithReauth();
  return retry429(result, 1);
};
